import { useState, FormEvent } from 'react';
import { FOOTER_CTA_CONTENT } from '@/content/features';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { signupForEarlyAccess } from '@/services/api';

/**
 * FooterCTA Component
 * Call-to-action card with email capture for early access
 * Integrated with Azure Functions backend
 */
export default function FooterCTA() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await signupForEarlyAccess(email);
      setIsSubmitted(true);
      setEmail('');

      // Reset after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:py-24 bg-brand-light dark:bg-secondary-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800 rounded-2xl p-8 md:p-12 shadow-soft-lg">
          {/* Content */}
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {FOOTER_CTA_CONTENT.title}
            </h2>
            <p className="text-lg text-white/90 mb-6">
              {FOOTER_CTA_CONTENT.subtitle}
            </p>
          </div>

          {/* Email Form */}
          {!isSubmitted ? (
            <>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4 max-w-md mx-auto">
                  <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
                </div>
              )}
              <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={FOOTER_CTA_CONTENT.placeholder}
                    required
                    className="w-full bg-white dark:bg-secondary-800 border-white/20"
                    aria-label="Email address"
                  />
                </div>
                <Button
                  type="submit"
                  variant="secondary"
                  size="md"
                  disabled={isSubmitting}
                  className="bg-white text-primary-700 hover:bg-white/90 dark:bg-secondary-800 dark:text-primary-400 dark:hover:bg-secondary-700"
                >
                  {isSubmitting ? 'Signing up...' : FOOTER_CTA_CONTENT.buttonText}
                </Button>
              </div>
            </form>
            </>
          ) : (
            <div className="text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 rounded-lg">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-white font-medium">
                  Thank you! Please check your email to confirm.
                </span>
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <p className="text-center text-sm text-white/80 mt-6">
            {FOOTER_CTA_CONTENT.disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
}

